<template>
  <section
    class="invoice-preview-wrapper"
  >
    <div
      class="invoice-preview d-flex flex-wrap"
    >
      <!-- NEW LAYOUT: Shown only when waybillId is 4 (100mm x 150mm - 1 per page) -->
      <div
        v-if="isWaybill4"
        class="invoice-preview-col d-flex flex-column align-items-center"
      >
        <div
          v-for="(dispatchNote, index) in waybill4Notes"
          :key="index"
          class="sq-card-container mb-3"
        >
          <!-- START: Al Sharqiya Express Label (100mm x 150mm) -->
          <div class="sq-label-wrapper">
            <!-- Header -->
            <div class="sq-header">
              <div>
                <h3 class="sq-brand-title">
                  {{ dispatchNote.merchant_business.business_name || 'N/A' }}
                </h3>
                <span class="sq-brand-subtitle">EXPRESS DELIVERY</span>
              </div>
              <div class="sq-header-right">
                <div class="sq-delivery-meta">
                  <div class="sq-delivery-type">
                    STANDARD
                  </div>
                  <div class="sq-delivery-date">
                    {{ formatThermalDate(dispatchNote.created_at) }}
                  </div>
                </div>
                <div class="sq-country-badge">
                  OM
                </div>
              </div>
            </div>

            <!-- Route Section -->
            <div class="sq-section sq-route-section">
              <div class="sq-route-container">
                <div class="sq-route-left">
                  <div class="sq-tag">
                    ROUTE / المسار
                  </div>
                  <div class="sq-route-code">
                    OMN-{{ dispatchNote.state.state_code }}-{{ dispatchNote.city.city_code }}
                  </div>
                  <div class="sq-hub-name">
                    OMAN · {{ dispatchNote.city.name.toUpperCase() }}
                  </div>
                </div>
                <div class="sq-bin-right">
                  <div class="sq-tag">
                    BIN
                  </div>
                  <div class="sq-bin-code">
                    {{ dispatchNote.warehouse.branch_code || 'N/A' }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Barcode Section -->
            <div class="sq-section sq-barcode-section">
              <div class="sq-barcode-wrapper">
                <Barcode
                  :id="dispatchNote.waybill_number"
                  :includetext="false"
                />
              </div>
              <div class="sq-waybill-number">
                {{ dispatchNote.waybill_number }}
              </div>
            </div>

            <!-- Recipient (TO) Section -->
            <div class="sq-section sq-recipient-section">
              <div class="sq-to-badge">
                TO
              </div>
              <div class="sq-recipient-detail">
                <div class="sq-recipient-name">
                  {{ dispatchNote.customer_name }}
                </div>
                <div class="sq-recipient-phone">
                  {{ dispatchNote.customer_phone }}
                </div>
                <div class="sq-recipient-addr-en">
                  {{ dispatchNote.customer_address }}
                </div>

                <div class="sq-dashed-divider" />
                <div class="sq-destination-summary">
                  {{ dispatchNote.warehouse.name }} · {{ dispatchNote.state.name }} · PC {{ dispatchNote.city.postal_code }}
                </div>
              </div>
            </div>

            <!-- FROM & Order Section -->
            <div class="sq-section sq-from-order-section">
              <div class="sq-from-details">
                <div class="sq-bold-tag">
                  FROM
                </div>
                <div class="sq-from-text">
                  {{ dispatchNote.merchant_business.business_name || 'N/A' }} · {{ dispatchNote.origin_city.name }} · {{ dispatchNote.merchant_business_address ? dispatchNote.merchant_business_address.phone : '---' }}
                </div>
              </div>
              <div class="sq-order-details">
                <div class="sq-tag">
                  ORDER
                </div>
                <div class="sq-order-number">
                  {{ dispatchNote.order_no || 'N/A' }}
                </div>
              </div>
            </div>

            <!-- COD & Weight Grid Section -->
            <div class="sq-section sq-cod-grid-section">
              <div class="sq-cod-col">
                <div class="sq-tag">
                  COLLECT ON DELIVERY · المبلغ المطلوب
                </div>
                <div class="sq-cod-amount-box">
                  <span class="sq-currency">OMR</span>
                  <span class="sq-amount">{{ dispatchNote.cod || 'N/A' }}</span>
                </div>
                <span class="sq-payment-badge">CASH / CARD</span>
              </div>
              <div class="sq-specs-col">
                <div class="sq-specs-row sq-specs-row-top">
                  <div>
                    <div class="sq-tag">
                      WEIGHT
                    </div>
                    <div class="sq-spec-val">
                      {{ dispatchNote.weight || 'N/A' }}
                    </div>
                  </div>
                  <div>
                    <div class="sq-tag">
                      PIECES
                    </div>
                    <div class="sq-spec-val text-left">
                      1
                    </div>
                  </div>
                </div>
                <div class="sq-specs-row sq-specs-row-bottom">
                  <div>
                    <div class="sq-tag sq-tag-declare">
                      DECLARED
                    </div>
                    <div class="sq-spec-val">
                      {{ dispatchNote.cod || 'N/A' }}
                    </div>
                  </div>
                  <div>
                    <div class="sq-tag">
                      DATE
                    </div>
                    <div class="sq-spec-val">
                      {{ __dateFormatterShort(dispatchNote.created_at) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Contents & Attempts & QR Section -->
            <div class="sq-section sq-contents-qr-section">
              <div class="sq-contents-left">
                <div class="sq-tag">
                  CONTENTS
                </div>
                <div class="sq-contents-text">
                  {{ dispatchNote.description ? dispatchNote.description.substring(0, 55) : 'N/A' }}
                </div>

                <div class="sq-attempts-row">
                  <div class="d-flex align-items-center">
                    <span class="sq-tag sq-tag-inline">ATTEMPT</span>
                    <div class="sq-attempt-boxes">
                      <span class="sq-attempt-box sq-attempt-active">1</span>
                      <span class="sq-attempt-box">2</span>
                      <span class="sq-attempt-box">3</span>
                    </div>
                  </div>
                  <div class="sq-tag sq-tag-inline">
                    OPENABLE · NO
                  </div>
                </div>
              </div>
              <div class="sq-qr-right">
                <div>
                  <QRcode :id="dispatchNote.waybill_number" />
                </div>
              </div>
            </div>

            <!-- Customer Receipt Section -->
            <div class="sq-section sq-receipt-section">
              <div class="sq-dashed-divider" />
              <div class="sq-receipt-header">
                CUSTOMER RECEIPT إيصال الاستلام
              </div>
              <div class="sq-receipt-body">
                <div class="sq-receipt-left">
                  <div class="sq-receipt-meta">
                    <span>{{ dispatchNote.waybill_number }}</span>
                    <span class="ml-2">OMR {{ dispatchNote.cod || 'N/A' }}</span>
                  </div>
                  <div class="sq-signature-box">
                    <span class="sq-tag sq-tag-inline">SIGNATURE</span>
                    <span class="sq-signature-line" />
                  </div>
                </div>
                <div class="sq-receipt-right">
                  <div>Support 800 7 4444</div>
                  <div>alsharqiya.com</div>
                </div>
              </div>
            </div>
          </div>
          <!-- END: Al Sharqiya Express Label -->
        </div>
      </div>

      <!-- Col: Left (Invoice Container) -->
      <div
        v-else-if="showDefaultLayout"
        class="invoice-preview-col"
      >
        <b-card
          v-for="(dispatch, index) in chunkedDispatchNotes"
          id="invoice-preview-card"
          :key="index"
          no-body
          class="invoice-preview-card"
          style="width:930px;"
        >
          <!-- Header -->
          <b-card-body
            class="invoice"
          >
            <b-row
              id="invoiceTableParent"
              class="d-flex flex-wrap"
            >
              <b-col
                v-for="(dispatchNote, index1) in dispatch"
                :id="dispatchNote.id"
                :key="index1"
              >
                <table
                  class="table-border"
                  style="width:430px;"
                >
                  <tr
                    class="table-padding"
                  >
                    <td>
                      <span class="brand-logo">
                        <b-img
                          class="dn-brand-logo"
                          :src="courier.company_logo ? `${bucketUrl}${courier.company_logo}` : defaultCompanyLogo"
                          alt="logo"
                        />
                      </span>
                    </td>
                    <td>
                      <div style="float: right">
                        <h5 class="dn-waybill">
                          Waybill ID : {{ dispatchNote.waybill_number }}
                        </h5>
                        <div
                          style="padding-right:10px;"
                        >
                          <Barcode
                            :id="dispatchNote.waybill_number"
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr
                    class="table-padding"
                    height="110px"
                  >
                    <td>
                      <h4
                        class="dn-business-name"
                      >
                        {{ courier.company_name }}
                      </h4>
                      <span class="dn-business-detail"> {{ courier.company_address }} <br> {{ courier.company_phone }} </span>
                    </td>
                    <td>
                      <div style="float: right">
                        <td>
                          <div>
                            <QRcode
                              :id="dispatchNote.waybill_number"
                            />
                          </div>
                        </td>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2">
                      <table width="100%">
                        <!-- row01 -->
                        <tr
                          class="dn-border-top"
                        >
                          <td
                            class="dn-border-right col-sm-6"
                          >
                            <h4 class="dn-sub-header">
                              Merchant Details
                            </h4>
                          </td>
                          <td class="col-sm-6">
                            <h4 class="dn-sub-header">
                              Customer Details
                            </h4>
                          </td>
                        </tr>
                        <!-- row02 -->
                        <tr
                          class="dn-border-top"
                          height="170px"
                        >
                          <td
                            class="dn-border-right col-sm-6"
                          >
                            <div class="dn-valign">
                              <h5 class="dn-content-header">
                                Name
                              </h5>
                              <span class="dn-content">{{ dispatchNote.merchant_business.business_name }}</span>
                            </div>
                            <div class="dn-valign">
                              <h5 class="dn-content-header">
                                Address
                              </h5>
                              <span class="dn-content">
                                {{ dispatchNote.merchant_business_address ? dispatchNote.merchant_business_address.address_line_1 : '---' }} <br>
                                {{ dispatchNote.merchant_business_address ? dispatchNote.merchant_business_address.address_line_2 : '---' }} <br>
                                {{ dispatchNote.merchant_business_address ? dispatchNote.merchant_business_address.address_line_3 : '---' }}
                              </span>
                            </div>
                            <div class="dn-valign">
                              <h5 class="dn-content-header">
                                Telephone
                              </h5>
                              <span class="dn-content">{{ dispatchNote.merchant_business_address ? dispatchNote.merchant_business_address.phone : '---' }}</span>
                            </div>
                          </td>
                          <td class="col-sm-6">
                            <div class="dn-valign">
                              <h5 class="dn-content-header">
                                Name
                              </h5>
                              <span class="dn-content">{{ getCustomerFieldValue(dispatchNote, 'customer_name') }}</span>
                            </div>
                            <div class="dn-valign">
                              <h5 class="dn-content-header">
                                Address
                              </h5>
                              <span class="dn-content text-break">{{ getCustomerFieldValue(dispatchNote, 'customer_address') }}</span>
                            </div>
                            <div class="dn-valign">
                              <h5 class="dn-content-header">
                                Telephone
                              </h5>
                              <span class="dn-content">
                                {{ getCustomerFieldValue(dispatchNote, 'concated_phone_number') }}
                              </span>
                            </div>
                          </td>
                        </tr>
                        <!-- row03 -->
                        <tr
                          class="dn-border-top"
                        >
                          <td colspan="2">
                            <h4 class="dn-sub-header">
                              Order Details
                            </h4>
                          </td>
                        </tr>
                        <!-- row04 -->
                        <tr class="dn-border-top">
                          <td
                            class="dn-border-right col-sm-6"
                            style="padding-bottom:10px;"
                          >
                            <div style="margin-top:6px">
                              <h5 class="dn-content-header">
                                Order Number
                              </h5>
                              <span class="dn-content">{{ dispatchNote.order_no }}</span>
                            </div>
                            <div>
                              <h5 class="dn-content-header">
                                Order Date
                              </h5>
                              <span class="dn-content">{{ __dateTimeFormatter(dispatchNote.created_at) }}</span>
                            </div>
                            <div>
                              <h5 class="dn-content-header">
                                Postal / Zip Code
                              </h5>
                              <span class="dn-content">{{ dispatchNote.city.postal_code }}</span>
                            </div>
                            <div>
                              <h5 class="dn-content-header">
                                Weight
                              </h5>
                              <span class="dn-content"> {{ dispatchNote.weight }}Kg </span>
                            </div>
                          </td>
                          <td class="d-flex flex-column justify-content-start w-100%">
                            <div style="height:70px;margin-top:6px">
                              <h5 class="dn-content-header">
                                Description
                              </h5>
                              <span class="dn-content">
                                {{ dispatchNote.description? dispatchNote.description.substring(0,55) : 'N/A' }}
                              </span>
                            </div>
                            <div style="margin-top:10px">
                              <h5 class="dn-content-header">
                                City
                              </h5>
                              <span class="dn-content">
                                {{ dispatchNote.city.name }}
                              </span>
                            </div>
                            <div>
                              <h5 class="dn-content-header">
                                Total COD
                              </h5>
                              <span class="dn-content">
                                Rs. {{ dispatchNote.cod }}
                              </span>
                            </div>
                          </td>
                        </tr>
                        <tr
                          class="dn-border-top"
                        >
                          <td
                            colspan="100%"
                            class="curfox-label font-small-2"
                          >
                            Powered By Curfox.com
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                </table>
              </b-col>
            </b-row>
            <!--        </div>-->
          </b-card-body>
        </b-card>
      </div>
      <div
        v-else
        id="custom-invoice-preview-col"
        class="invoice-preview-col"
      />
      <!-- Right Col: Card -->
      <b-col
        class="invoice-actions"
      >
        <b-card>
          <!-- Button: Print -->
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-warning"
            class="mb-75"
            block
            @click="printInvoice"
          >
            Print
          </b-button>
        </b-card>
      </b-col>
    </div>
  </section>
</template>

<script>
import {
  BRow, BCol, BCard, BCardBody, BButton, BImg,
} from 'bootstrap-vue'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Vue from 'vue'
import Ripple from 'vue-ripple-directive'
import _ from 'lodash'
import { bucketUrl, defaultCompanyLogo } from '@/constants/config'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import Barcode from '@/components/print/Barcode.vue'
import QRcode from '@/components/print/QRcode.vue'

const OrderRepository = RepositoryFactory.get('order')
const LayoutRepository = RepositoryFactory.get('layout')

export default {
  directives: {
    Ripple,
  },
  components: {
    QRcode,
    Barcode,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BButton,
    BImg,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      courier: {},
      dispatchNotes: [],
      bucketUrl,
      defaultCompanyLogo,
      showDefaultLayout: false,
      waybillLayout: null,
      loading: false,
      waybillsPerCanvas: 4,
    }
  },
  computed: {
    isWaybill4() {
      return String(this.$route.params.waybillId) === '4'
    },
    waybill4Notes() {
      return this.dispatchNotes && this.dispatchNotes.length > 0
        ? this.dispatchNotes
        : [{ id: 1, waybill_number: 'ASE0004257909' }]
    },
    chunkedDispatchNotes() {
      return _.chunk(this.dispatchNotes, this.waybillsPerCanvas)
    },
  },
  async mounted() {
    this.courier = JSON.parse(localStorage.getItem('businessSettings'))
    await this.getOrdersByWaybills()
    if (this.isWaybill4) return
    await this.getWaybillLayout()
    if (this.waybillLayout) {
      this.initWaybillLayout()
    } else {
      this.showDefaultLayout = true
    }
  },
  methods: {
    async getOrdersByWaybills() {
      try {
        this.loading = true
        const { data } = (await OrderRepository.getPODOrdersByWaybills(this.$route.params.selectedWaybills))
        this.dispatchNotes = data.data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    async getWaybillLayout() {
      try {
        this.loading = true
        const { data } = (await LayoutRepository.getWaybillLayoutDetail(this.$route.params.waybillId))
        this.waybillLayout = data.data.template
        this.waybillsPerCanvas = data.data.waybills_per_canvas
      } catch (e) {
        this.showErrorMessage('Couldn\'t fetch waybill layout!')
      }
      this.loading = false
    },
    initWaybillLayout() {
      const container = document.getElementById('custom-invoice-preview-col')
      const removals = ['draggable', 'resizable', 'resize-drag', 'resize-drag-vertical', 'resize-drag-horizontal', 'contenteditable']
      removals.forEach(removal => {
        this.waybillLayout = this.waybillLayout.replaceAll(removal, '')
      })
      this.chunkedDispatchNotes.forEach(chunk => {
        const tempElement = document.createElement('div')
        tempElement.classList.add('invoice-preview-card')
        tempElement.style.width = '930px'
        tempElement.innerHTML = this.waybillLayout
        container.appendChild(tempElement)
        const outOfPlaceList = []
        document.getElementById('invoiceTableParent').children.forEach(child => {
          if (child.tagName !== 'TABLE') {
            const copy = child.cloneNode(true)
            outOfPlaceList.push(copy)
          } else {
            document.getElementById('invoiceTableParent').innerHTML = child.outerHTML
          }
        })
        const tableParent = document.getElementById('invoiceTableParent')
        const tableInnerHTML = document.getElementById('invoiceTableParent').innerHTML
        document.getElementById('invoiceTable').remove()
        document.querySelectorAll('.customComponent').forEach(element => {
          element.remove()
        })
        if (document.querySelector('#barcode_CF000000')) {
          document.querySelector('#barcode_CF000000').remove()
        }
        if (document.querySelector('#qrcode_CF000000')) {
          document.querySelector('#qrcode_CF000000').remove()
        }
        chunk.map(async dispatchNote => {
          const tableToModify = tableInnerHTML
          const invoiceElement = document.createElement('div')
          invoiceElement.classList.add('col')
          invoiceElement.innerHTML = tableToModify
          tableParent.appendChild(invoiceElement)
          const newTable = document.getElementById('invoiceTable')
          // eslint-disable-next-line array-callback-return
          outOfPlaceList.map(child => {
            newTable.insertAdjacentElement('afterbegin', child)
          })
          newTable.id = `id${(new Date()).getTime()}`
          newTable.classList.add('invoiceTable')
          tableParent.id = `id${(new Date()).getTime()}`
          const mappers = [
            {
              key: '-- business_name --',
              value: dispatchNote.merchant_business.business_name || 'N/A',
            },
            {
              key: '-- address_line_1 --',
              value: dispatchNote.merchant_business_address ? dispatchNote.merchant_business_address.address_line_1 : '---',
            },
            {
              key: '-- address_line_2 --',
              value: dispatchNote.merchant_business_address ? dispatchNote.merchant_business_address.address_line_2 : '---',
            },
            {
              key: '-- address_line_3 --',
              value: dispatchNote.merchant_business_address ? dispatchNote.merchant_business_address.address_line_3 : '---',
            },
            {
              key: '-- phone --',
              value: dispatchNote.merchant_business_address ? dispatchNote.merchant_business_address.phone : '---',
            },
            {
              key: '-- customer_name --',
              value: this.getCustomerFieldValue(dispatchNote, 'customer_name') || 'N/A',
            },
            {
              key: '-- customer_address --',
              value: this.getCustomerFieldValue(dispatchNote, 'customer_address') || 'N/A',
            },
            {
              key: '-- concated_phone_number --',
              value: this.getCustomerFieldValue(dispatchNote, 'concated_phone_number') || 'N/A',
            },
            {
              key: '-- customer_secondary_phone --',
              value: this.getCustomerFieldValue(dispatchNote, 'customer_secondary_phone') || '',
            },
            {
              key: '-- waybill_number --',
              value: dispatchNote.waybill_number || 'N/A',
            },
            {
              key: '-- order_no --',
              value: dispatchNote.order_no || 'N/A',
            },
            {
              key: '-- created_at --',
              // eslint-disable-next-line no-underscore-dangle
              value: this.__dateTimeFormatter(dispatchNote.created_at),
            },
            {
              key: '-- city.postal_code --',
              value: dispatchNote.city.postal_code || 'N/A',
            },
            {
              key: '-- weight --',
              value: dispatchNote.weight || 'N/A',
            },
            {
              key: '-- description --',
              value: dispatchNote.description ? dispatchNote.description.substring(0, 55) : 'N/A',
            },
            {
              key: '-- city.name --',
              value: dispatchNote.city.name || 'N/A',
            },
            {
              key: '-- warehouse.name --',
              value: dispatchNote.warehouse.name || 'N/A',
            },
            {
              key: '-- cod --',
              value: dispatchNote.cod || 'N/A',
            },
            {
              key: '-- delivery_charge --',
              value: dispatchNote.delivery_charge || 'N/A',
            },
          ]
          mappers.forEach(mapper => {
            newTable.innerHTML = newTable.innerHTML.replaceAll(mapper.key, mapper.value)
          })
          newTable.innerHTML = newTable.innerHTML.replaceAll('customComponent', '')
          if (this.waybillLayout.includes('barcode_CF000000')) {
            const barcodeStyle = document.getElementById('barcode_CF000000').style.cssText
            new Vue({
              ...Barcode,
              propsData: {
                id: dispatchNote.waybill_number,
              },
            }).$mount('#barcode_CF000000')
            document.getElementById(`barcode_${dispatchNote.waybill_number}`).style.cssText = barcodeStyle
          }
          if (this.waybillLayout.includes('qrcode_CF000000')) {
            const qrStyle = document.getElementById('qrcode_CF000000').style.cssText
            new Vue({
              ...QRcode,
              propsData: {
                id: dispatchNote.waybill_number,
              },
            }).$mount('#qrcode_CF000000')
            document.getElementById(`qrcode_${dispatchNote.waybill_number}`).style.cssText = qrStyle
          }
        })
      })
    },
    getCustomerFieldValue(dispatchNote, fieldName) {
      if (!dispatchNote) return ''
      if (dispatchNote.is_encrypted) {
        if (this.$can && this.$can('show-decrypted-orders')) {
          return dispatchNote[fieldName] || ''
        }
        return '******'
      }
      return dispatchNote[fieldName] || ''
    },
    async printInvoice() {
      window.print()
    },
    formatThermalDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      if (Number.isNaN(date.getTime())) return dateString

      const day = String(date.getDate()).padStart(2, '0')
      const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
      const month = months[date.getMonth()]
      const year = date.getFullYear()

      return `${day} ${month} ${year}`
    },
  },
}
</script>

<style lang="scss">
@import "../../@core/scss/base/pages/app-invoice.scss";

@page {
    size: A4 portrait;
    margin: 10mm;
  }
.dn-business-name {
  padding-left: 15px;
  font-size: 17px;
  font-weight: bolder;
  color: black;
}
.dn-business-detail {
  display: inline-block;
  padding-left: 15px;
  font-size: 13px;
  color: black;
}
.dn-brand-logo {
  display: block;
  width: 90px;
  height: 90px;
  margin-top: 10px;
  margin-left: 10px;
  margin-right: 10px;
  float: left;
  border-radius: 20px;
}
.dn-border-top {
  border-top: solid black 2px;
}
.dn-border-right {
  border-right: solid black 2px;
}
.dn-border-bottom {
  border-bottom: solid black 2px;
}
.dn-border-left {
  border-left: solid black 2px;
}

.dn-waybill {
  padding: 10px 5px 5px 0;
  font-weight: bolder;
  color: black;
  text-align: right;
  font-size: 15px;
}
.dn-sub-header {
  padding-top: 5px;
  text-align: center;
  font-size: 13px;
  font-weight: bolder;
  color: black;
}
.dn-city-cod {
  padding: 5px 0 0 5px;
  font-size: 18px;
  font-weight: bolder;
  color: black;
  text-align: center;
}
.dn-content-header {
  padding: 3px 0 0 5px;
  font-size:13px;
  font-weight: bolder;
  margin-bottom: 2px;
}
.dn-content {
  padding-left: 5px;
  font-size: 13px;
  display:inline-block;
  color: black;
  font-weight: 400;
}
.dn-valign {
  vertical-align: top;
}
.invoice-preview-col {
  overflow-y: auto;
}
.table-border {
  border: solid black 2px;
  margin-bottom:15px;
  margin-top:10px
}
table {
    table-layout:auto !important;
}
.curfox-label {
    padding: 2px;
    text-align: center;
    font-weight: bolder;
    column-span: all !important;
    color: black;
}
.black-components,
.dn-business-name,
.dn-business-detail,
.dn-waybill,
.dn-sub-header,
.dn-city-cod,
.dn-content-header,
.dn-content,
.dn-valign,
.curfox-label {
  font-family: "Arial", sans-serif;
  color: black;
}

@media print {
  // Global Styles
  .col-sm-6 {
    width: 25mm;
  }
  body {
    width: 210mm;
    height: 296mm;
    overflow:hidden;
  }
  .custom-h5.dn-waybill {
    font-size: 16px !important;
    color: black !important;
    font-weight: bold !important;
  }
 .invoiceTable {
    outline: solid black 3px !important;
    border: none !important;
  }
  nav.header-navbar {
    display: none;
  }
  .main-menu {
    display: none;
  }
  .header-navbar-shadow {
    display: none !important;
  }
  .content-header {
    display: none;
  }
  .content.app-content {
    margin: 0;
    padding-top: 2rem !important;
  }
  footer.footer {
    display: none;
  }
  .card {
    background-color: transparent;
    box-shadow: none;
    overflow:hidden;
  }
  .customizer-toggle {
    display: none !important;
  }
  // Invoice Specific Styles
  .invoice-preview-wrapper {
    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }
  .invoice-preview {
    display: block;
  }
  .invoice {
    position: relative;
    display: block;
  }
  .invoice-preview-col {
    display: block;
  }
  .invoice-preview-card {
    display: block;
    break-inside: avoid;
    page-break-inside: avoid;
    break-after: always;
    page-break-after: always;
  }
  section span, section p, section div {
    font-weight: 600 !important;
  }
  .black-components,
  .dn-business-name,
  .dn-business-detail,
  .dn-waybill,
  .dn-sub-header,
  .dn-city-cod,
  .dn-content-header,
  .dn-content,
  .dn-valign,
  .curfox-label {
    font-family: "Arial", sans-serif;
    color: black;
  }

  .sq-card-container {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    margin: 0 !important;
    padding: 0 !important;

    &:not(:last-child) {
      page-break-after: always !important;
      break-after: always !important;
    }
  }

  .sq-label-wrapper {
    width: 146mm !important;
    height: 216mm !important;
    margin: 0 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}

/* =========================================
   NEW CLASSES FOR WAYBILL ID 4 LAYOUT ONLY
   Dimensions: 100mm x 150mm (Single Per Page)
   ========================================= */
.sq-card-container {
  width: 146mm !important;
  height: 216mm !important;
  page-break-inside: avoid;
  break-inside: avoid;

  &:not(:last-child) {
    page-break-after: always;
    break-after: always;
  }
}

.sq-label-wrapper {
  width: 146mm;
  height: 216mm;
  padding: 2mm;
  background-color: #ffffff;
  color: #000000;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .sq-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 15px;
  }

  .sq-brand-title {
    font-weight: 900;
    font-size: 18px;
    letter-spacing: 0.5px;
    color: #000000;
    margin: 0;
    line-height: 1;
  }

  .sq-brand-subtitle {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: #000000;
    display: block;
    margin-top: 2px;
  }

  .sq-header-right {
    display: flex;
    align-items: center;
  }

  .sq-delivery-meta {
    margin-right: 8px;
    text-align: right;
  }

  .sq-delivery-type {
    font-weight: 900;
    font-size: 18px;
    color: #000000;
    line-height: 1;
  }

  .sq-delivery-date {
    font-size: 13px;
    font-weight: 700;
    color: #000000;
    margin-top: 5px;
  }

  .sq-country-badge {
    background-color: #000000;
    color: #ffffff;
    font-weight: 900;
    font-size: 18px;
    padding: 2px 8px;
    line-height: 1.1;
  }

  .sq-section {
    width: 100%;
  }

  .sq-route-section {
    border-top: 3px solid #000000;
    border-bottom: 3px solid #000000;
  }

  .sq-route-container {
    display: flex;
    align-items: stretch;
  }

  .sq-route-left {
    flex-grow: 1;
    padding-right: 8px;
    padding-top: 5px;
    padding-bottom: 2px;
  }

  .sq-route-code {
    font-size: 30px;
    font-weight: 900;
    line-height: 1;
    letter-spacing: -0.5px;
    color: #000000;
    padding: 5px 0;
  }

  .sq-hub-name {
    font-size: 15px;
    font-weight: 800;
    color: #000000;
    margin-top: 2px;
  }

  .sq-bin-right {
    border-left: 2px solid #000000;
    min-width: 80px;
    padding-left: 6px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .sq-bin-code {
    font-size: 23px;
    font-weight: 900;
    line-height: 1.1;
    color: #000000;
  }

  .sq-barcode-section {
    text-align: center;
    padding: 10px 0 6px 0;
  }

  .sq-barcode-wrapper {
    display: flex;
    justify-content: center;
    svg, canvas, img {
      height: 90px !important;
      width: 180px !important;
      max-width: 100%;
    }
  }

  .sq-waybill-number {
    font-size: 18px;
    font-weight: 900;
    color: #000000;
    margin-top: 2px;
  }

  .sq-recipient-section {
    border-top: 3px solid #000000;
    padding-top: 6px;
    display: flex;
    align-items: flex-start;
    margin-bottom: 5px;
    width: 100%;
  }

  .sq-to-badge {
    background-color: #000000;
    color: #ffffff;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 900;
    flex-shrink: 0;
  }

  .sq-recipient-detail {
    margin-left: 8px;
    width: 100% !important;
  }

  .sq-recipient-name {
    font-size: 18px;
    font-weight: 900;
    color: #000000;
  }

  .sq-recipient-phone {
    font-size: 18px;
    font-weight: 900;
    color: #000000;
    margin-bottom: 2px;
  }

  .sq-recipient-addr-en {
    font-size: 15px;
    font-weight: 700;
    color: #000000;
    line-height: 1.25;
    margin-bottom: 20px;
  }

  .sq-recipient-addr-ar {
    font-size: 12px;
    font-weight: 700;
    color: #000000;
    line-height: 1.25;
    text-align: right;
    margin-top: 2px;
    margin-bottom: 10px;
  }

  .sq-destination-summary {
    font-size: 15px;
    font-weight: 900;
    color: #000000;
    text-align: left;
    letter-spacing: 0.3px;
  }

  .sq-from-order-section {
    border-top: 2px solid #000000;
    border-bottom: 3px solid #000000;
    padding: 5px 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .sq-from-text {
    font-size: 14px;
    font-weight: 700;
    color: #000000;
  }

  .sq-order-details {
    text-align: right;
  }

  .sq-order-number {
    font-size: 14px;
    font-weight: 700;
    color: #000000;
  }

  .sq-cod-grid-section {
    border-bottom: 3px solid #000000;
    padding: 0;
    display: flex;
    justify-content: space-between;
  }

  .sq-cod-col {
    padding: 6px 0;
    flex-grow: 1;
  }

  .sq-cod-amount-box {
    display: flex;
    margin: 2px 0;
    line-height: 1;
  }

  .sq-currency {
    font-size: 15px;
    font-weight: 900;
    margin-right: 4px;
    color: #000000;
  }

  .sq-amount {
    font-size: 23px;
    font-weight: 900;
    color: #000000;
  }

  .sq-payment-badge {
    background-color: #000000;
    color: #ffffff;
    font-size: 15px;
    font-weight: 900;
    padding: 2px 6px;
    display: inline-block;
    border-radius: 2px;
    letter-spacing: 0.5px;
    margin: 10px 0;
  }

  .sq-specs-col {
    min-width: 130px;
    padding: 6px 0;
    border-left: 2px solid #000000;
  }

  .sq-specs-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 2px;
    margin-left: 4px;
  }

  .sq-specs-row-top {
    margin-right: 16px;
  }

  .sq-specs-row-bottom {
    border-top: 1px solid #000000;
    padding-top: 4px;
    margin-bottom: 0;
  }

  .sq-spec-val {
    font-size: 15px;
    font-weight: 900;
    color: #000000;
    line-height: 1.2;
  }

  .sq-contents-qr-section {
    border-bottom: 2px solid #000000;
    padding-bottom: 6px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  .sq-contents-left {
    flex-grow: 1;
    padding-right: 8px;
  }

  .sq-contents-text {
    font-size: 15px;
    font-weight: 900;
    color: #000000;
    margin-bottom: 30px;
    line-height: 1.25;
  }

  .sq-attempts-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .sq-attempt-boxes {
    display: flex;
    margin-right: 8px;
  }

  .sq-attempt-box {
    border: 2px solid #000000;
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 900;
    font-size: 11px;
    margin-right: 3px;
    color: #000000;
  }

  .sq-attempt-active {
    background-color: #ffffff;
  }

  .sq-qr-right {
    display: flex;
    align-items: flex-end;
  }

  .sq-receipt-section {
    border: none !important;
    padding-top: 2px;
  }

  .sq-receipt-header {
    font-size: 15px;
    font-weight: 700;
    color: #000000;
    margin-bottom: 2px;
  }

  .sq-receipt-body {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  .sq-receipt-left {
    flex-grow: 1;
    padding-right: 8px;
  }

  .sq-receipt-meta {
    font-size: 15px;
    font-weight: 700;
    color: #000000;
  }

  .sq-signature-box {
    display: flex;
    align-items: center;
    margin-top: 10px;
    padding-left: 120px;
  }

  .sq-signature-line {
    border-bottom: 2px solid #000000;
    flex-grow: 1;
    margin-left: 6px;
    height: 1px;
    display: inline-block;
    margin-top: 20px;
  }

  .sq-receipt-right {
    font-size: 13px;
    font-weight: 700;
    text-align: right;
    color: #000000;
  }

  .sq-dashed-divider {
    border-top: 2px dashed #000000;
    width: 100%;
    margin: 8px 0;
  }

  .sq-tag {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    color: #000000;
    line-height: 1.1;
    margin-bottom: 4px;
  }

  .sq-tag-declare {
    margin-right: 10px;
  }

  .sq-tag-inline {
    margin-bottom: 0;
    margin-right: 4px;
  }

  .sq-bold-tag {
    font-size: 13px;
    font-weight: 700;
    color: #000000;
  }
}
</style>
